import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useParams} from "react-router-dom";
import { toast } from 'react-toastify';

// Hlavní hooky
import { useAuth, ROLES } from '../components/auth/AuthContext';
import { useQuoteData } from '../hooks/useQuoteData';
import { useQuoteImporters } from '../hooks/useQuoteImporters';
import { useQuoteActions } from '../hooks/useQuoteActions';

// Komponenty
import QuoteGrid from '../components/QuoteGrid';
import ItemActionsPanel from '../components/panels/ItemActionsPanel';
import PurchasePanel from '../components/panels/PurchasePanel';
import SalesPanel from '../components/panels/SalesPanel';
import ItemDetailPanel from '../components/ItemDetailPanel';
import LoadingSpinner from '../components/LoadingSpinner';
import AddonTextsEditor from '../components/AddonTextsEditor';
import NewItemModal from '../components/NewItemModal';
import ProductSearchModal from '../components/ProductSearchModal';
import TargetPriceModal from '../components/TargetPriceModal';

// Styly a obrázky
import './QuoteEditor.css';
// Images in public should be referenced via PUBLIC_URL, not imported
const quoteMachineLogo = process.env.PUBLIC_URL + '/images/QuoteMachine.png';

// Pomocné funkce
const formatCurrencyDynamic = (value, currencyCode = 'EUR', exchangeRate = 1) => { if (value == null || isNaN(value)) return ''; const finalValue = value * exchangeRate; return new Intl.NumberFormat('cs-CZ', { style: 'currency', currency: currencyCode, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(finalValue); };
const getHeaderColor = (step) => {
    switch (step) {
        case 1: return '#27498f'; case 2: return '#8f2726'; 
        case 3: return '#226A6E'; case 4: return '#6e278f'; 
        default: return '#FFFFFF';
    }
};

const StepIndicator = ({ currentStep, onStepClick, allowedSteps }) => {
    const steps = ["Engineering", "Purchase", "Quotation", "Addon Texts"];
    return (
        <div className="step-indicator">
            <div className="step-indicator-main">
                {steps.map((title, index) => {
                    const stepNumber = index + 1;
                    const isActive = currentStep === stepNumber;
                    const isCompleted = currentStep > stepNumber;
                    const isAllowed = allowedSteps.includes(stepNumber);
                    return (
                        <React.Fragment key={stepNumber}>
                            <div 
                                className={`step-circle ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''} ${!isAllowed ? 'disabled' : ''}`}
                                onClick={() => isAllowed && onStepClick(stepNumber)}
                                title={isAllowed ? `Go to Step ${stepNumber}: ${title}` : 'Access Denied'}
                            >
                                {isCompleted ? '✔' : stepNumber}
                            </div>
                            {stepNumber < steps.length && <div className={`step-line ${isCompleted ? 'completed' : ''}`}></div>}
                        </React.Fragment>
                    );
                })}
            </div>
            <h2 className="step-title-header">{`Step ${currentStep}: ${steps[currentStep - 1]}`}</h2>
        </div>
    );
};

function QuoteEditor() {
    const { quoteId } = useParams();
    const gridRef = useRef();
    const fileInputRef = useRef(null);

    // 1. Oprávnění
    const { currentUser } = useAuth();
    const userRole = currentUser?.roleName;
    const userRoleId = currentUser?.roleId;
    const PERMISSIONS = useMemo(() => ({
        [ROLES.KD_SALES]: [1, 2, 3, 4],
        [ROLES.KD_PURCHASE]: [1, 2, 4],
        [ROLES.KD_ENGINEERING]: [1, 4]
    }), []);
    const allowedSteps = useMemo(() => PERMISSIONS[userRoleId] || [], [userRoleId, PERMISSIONS]);

    // 2. Data
    const {
        headerData, setHeaderData, rowData, setRowData, addonTexts, setAddonTexts, textTemplates,
        loading, error, isSaving, isRefreshing, isSyncingToCRM, hasLocalChanges, associatedCosts, setAssociatedCosts,
        costDistribution, setCostDistribution, fetchQuoteData, handleSaveChanges,
        handleRefreshItemsFromCRM, handleSyncItemsToCRM
    } = useQuoteData(quoteId);

    // 3. Importy
    const { handleInnomoticsQuoteImport, handleInnomoticsCartImport, handleSiemensImport } = useQuoteImporters(setRowData, rowData, headerData, quoteId);

    // 4. Akce a stavy pro UI
    const {
        isProductSearchOpen, setIsProductSearchOpen, isNewItemModalOpen, setIsNewItemModalOpen,
        selectedItem, setSelectedItem, bulkQuantity, setBulkQuantity, bulkOPDiscount, setBulkOPDiscount,
        bulkRPDiscount, setBulkRPDiscount, bulkCPDiscount, setBulkCPDiscount, targetMarginInput, setTargetMarginInput,
        isDuplicating,
        isTargetPriceModalOpen, itemForTargetPriceModal,
        handleOpenTargetPriceModal, handleSaveTargetPrice,
        handleCloseTargetPriceModal, // --- NAČTENÍ NOVÉ FUNKCE ---
        handleShowItemDetails, handleSaveItemDetail, handleProductSelect, handleAddNewItem,
        handleBulkUpdateQuantity, handleBulkUpdateOPDiscount, handleBulkUpdateRPDiscount, handleBulkUpdateCPDiscount,
        handleSetTargetMargin, handleDeleteSelectedRows, handleRenumberSequence, handleDuplicate
    } = useQuoteActions(quoteId, headerData, rowData, setRowData, addonTexts, gridRef, { hasLocalChanges });
    
    // 5. Navigace a lokální stavy
    const [currentStep, setCurrentStep] = useState(1);
    const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
    
    const handleGoToStep = useCallback((step) => {
        if (allowedSteps.includes(step)) setCurrentStep(step);
        else toast.warn("You do not have permission to access this step.");
    }, [allowedSteps]);

    const handleSmartBack = useCallback(() => {
        const currentIndex = allowedSteps.indexOf(currentStep);
        if (currentIndex > 0) setCurrentStep(allowedSteps[currentIndex - 1]);
    }, [currentStep, allowedSteps]);
    
    const handleGoToNext = useCallback(() => {
        const currentIndex = allowedSteps.indexOf(currentStep);
        if (currentIndex < allowedSteps.length - 1) {
            setCurrentStep(allowedSteps[currentIndex + 1]);
        } else {
            toast.info('No further step available for your role.');
        }
    }, [currentStep, allowedSteps]);
    
    const handleResetChanges = useCallback(() => {
        if (window.confirm("Are you sure you want to discard all unsaved changes?")) fetchQuoteData(quoteId, true);
    }, [quoteId, fetchQuoteData]);

    // Warn on page unload/navigation if there are unsaved local changes OR not-synced-to-CRM changes
    useEffect(() => {
        const handler = (e) => {
            const crmDirty = headerData && headerData.Items_Synced === false; // server marks whether items were synced
            if (hasLocalChanges || crmDirty) {
                e.preventDefault();
                e.returnValue = '';
                return '';
            }
            return undefined;
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [hasLocalChanges, headerData]);

    const crmDirtyForPrompt = !!(headerData && headerData.Items_Synced === false);

    // ---- Button visual states ----
    const isLocalDirty = hasLocalChanges;
    // Highlight Save to CRM when either local changes exist (it will perform local save) OR Items_Synced is false
    const isCrmDirty = hasLocalChanges || crmDirtyForPrompt;
    const saveBtnClass = isSaving ? 'btn-success' : (isLocalDirty ? 'btn-orange' : 'btn-secondary');
    const saveBtnTitle = isSaving ? 'Saving your local changes…' : (isLocalDirty ? 'You have unsaved local changes' : 'All local changes saved');
    const saveToCrmBtnClass = isSyncingToCRM ? 'btn-success' : (isCrmDirty ? 'btn-info' : 'btn-primary-inverted');
    const saveToCrmBtnTitle = isSyncingToCRM ? 'Saving to CRM…' : (isCrmDirty ? 'Changes not yet synchronized to CRM' : 'Everything is up to date in CRM');

    const handleOpenWorkDrive = useCallback(async () => {
        if (!quoteId) { toast.error('Quote ID is missing, cannot open WorkDrive.'); return; }
        const openInternal = (folderId) => {
            if (!folderId) { toast.info('WorkDrive folder is being prepared. Please try again in a few seconds.'); return; }
            const internalUrl = `https://workdrive.zoho.eu/folder/${folderId}`;
            window.open(internalUrl, '_blank');
        };
        try {
            // If we already have the folder ID in header, prefer opening directly
            if (headerData?.Workdrive_Folder_ID) {
                return openInternal(headerData.Workdrive_Folder_ID);
            }
            // Otherwise, ask backend to refresh/get the folder and open internal path
            const resp = await fetch('/server/quote_service/', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'get_or_refresh_workdrive', quoteCrmId: quoteId })
            });
            if (!resp.ok) {
                const txt = await resp.text();
                throw new Error(`Server responded ${resp.status}: ${txt?.slice(0,200)}`);
            }
            const data = await resp.json();
            if (data?.ready && data?.folderId) {
                // update local header cache and open internal workdrive route
                setHeaderData(prev => ({ ...prev, Workdrive_Folder_ID: data.folderId, Workdrive_Share_Link: data.link }));
                openInternal(data.folderId);
            } else {
                toast.info('WorkDrive folder is being prepared. Please try again in a few seconds.');
            }
        } catch (e) {
            toast.error(`WorkDrive error: ${e.message}`);
        }
    }, [quoteId, headerData, setHeaderData]);

    const handlePreviewPDF = useCallback(async () => {
        if (!quoteId) { toast.error("Quote ID is missing, cannot generate PDF."); return; }
        setIsGeneratingPDF(true);
        const toastId = toast.loading("Generating PDF preview...");
        try {
            const saveSuccess = await handleSaveChanges(false);
            if (!saveSuccess) {
                throw new Error("Could not save changes before generating PDF. Please try again.");
            }
            const response = await fetch('/server/pdf_service/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'preview', quoteCrmId: quoteId })
            });
            if (!response.ok) {
                const errorText = await response.text();
                let errorMessage = "Unknown error during PDF generation.";
                try {
                    const errorData = JSON.parse(errorText);
                    errorMessage = errorData.details || errorData.error;
                } catch (e) {
                    errorMessage = errorText.substring(0, 200);
                }
                throw new Error(errorMessage);
            }
            const blob = await response.blob();
            const fileURL = URL.createObjectURL(blob);
            window.open(fileURL, '_blank');
            toast.update(toastId, { render: "PDF preview is ready!", type: "success", isLoading: false, autoClose: 3000 });
        } catch (err) {
            console.error("PDF Preview Error:", err);
            toast.update(toastId, { render: `PDF Generation Error: ${err.message}`, type: "error", isLoading: false, autoClose: 5000 });
        } finally {
            setIsGeneratingPDF(false);
        }
    }, [quoteId, handleSaveChanges]);

    const handleCellValueChanged = useCallback((event) => {
        setRowData(prevRowData => prevRowData.map(row => row.ROWID === event.node.data.ROWID ? event.node.data : row));
    }, [setRowData]);
    
    const onRowDragEnd = useCallback(() => {
        handleRenumberSequence();
    }, [handleRenumberSequence]);

    const handleAssociatedCostChange = (field, value) => {
        setAssociatedCosts(costs => ({ ...costs, [field]: value }));
    };

    const handleHeaderChange = (e) => { 
        setHeaderData(prevData => ({ ...prevData, [e.target.name]: e.target.value })); 
    };

    const actionMenuConfig = [
        { label: "Add", subItems: [{ label: "New (Blank)", onClick: () => setIsNewItemModalOpen(true) }, { label: "Link from CRM", onClick: () => setIsProductSearchOpen(true) }] },
        { label: "Import", subItems: [
            { label: "Siemens Mall (XML)", onClick: () => { fileInputRef.current.onchange = (e) => handleSiemensImport(e.target.files[0]); fileInputRef.current.accept = '.xml'; fileInputRef.current.click(); }},
            { label: "Innomotics Quote (XLSX)", onClick: () => { fileInputRef.current.onchange = (e) => handleInnomoticsQuoteImport(e.target.files[0]); fileInputRef.current.accept = '.xls,.xlsx'; fileInputRef.current.click(); }},
            { label: "Innomotics Cart (XLSX)", onClick: () => { fileInputRef.current.onchange = (e) => handleInnomoticsCartImport(e.target.files[0]); fileInputRef.current.accept = '.xls,.xlsx'; fileInputRef.current.click(); }}
        ]},
        { label: "Manage", subItems: [{ label: "Save Sequence", onClick: handleRenumberSequence }, { label: "Delete Selected", onClick: handleDeleteSelectedRows }] },
        { label: "Duplicate", subItems: [{ label: "as New Version", onClick: () => handleDuplicate('version') }, { label: "as New Alternative", onClick: () => handleDuplicate('alternative') }] },
        { label: "WorkDrive", subItems: [{ label: "Open Folder", onClick: handleOpenWorkDrive }] }
    ];

    const totals = useMemo(() => { 
        let totalQuantity = 0; let totalGoodsEur = 0; let totalCPEur = 0;
        let totalAsCosts = Object.values(associatedCosts).reduce((sum, cost) => sum + (parseFloat(cost) || 0), 0);
        const dataToIterate = Array.isArray(rowData) ? rowData : []; 
        dataToIterate.forEach(item => { 
            const quantity = parseFloat(item.Quantity) || 0; 
            const listPrice = parseFloat(item.List_Price) || 0; 
            const opDiscount = parseFloat(item.OP_Discount) || 0; 
            const rpDiscount = parseFloat(item.RP_Discount) || 0; 
            const cpDiscount = parseFloat(item.CP_Discount) || 0; 
            totalQuantity += quantity; 
            const opPrice = listPrice * (1 - opDiscount / 100); 
            totalGoodsEur += opPrice * quantity; 
            const rpPrice = listPrice * (1 - rpDiscount / 100); 
            const cpPrice = rpPrice * (1 - cpDiscount / 100); 
            totalCPEur += cpPrice * quantity; 
        }); 
        const totalFinalOPEur = totalGoodsEur + totalAsCosts;
        
        // Calculate discount amount (either percentage or fixed)
        const discountRel = parseFloat(headerData?.DiscountRel) || 0;
        const discountAbs = parseFloat(headerData?.DiscountAbs) || 0;
        const discountAmountEur = discountAbs > 0 ? discountAbs : (totalCPEur * discountRel / 100);
        
        // CP Total after discount
        const cpTotalAfterDiscount = totalCPEur - discountAmountEur;
        
        // Margin is calculated AFTER discount
        const totalMarginAbsEur = cpTotalAfterDiscount - totalFinalOPEur; 
        const totalMarginRel = totalFinalOPEur === 0 ? 0 : (totalMarginAbsEur / totalFinalOPEur) * 100; 
        
        // Shipping is added AFTER margin calculation (not included in margin)
        const shipping = parseFloat(headerData?.Shipping) || 0;
        const grandTotal = cpTotalAfterDiscount + shipping;
        
        return { 
            totalQuantity, totalGoodsEur, totalAsCosts, totalFinalOPEur, totalCPEur, 
            discountAmountEur, cpTotalAfterDiscount, 
            totalMarginAbsEur, totalMarginRel, 
            shipping, grandTotal 
        }; 
    }, [rowData, associatedCosts, headerData?.DiscountRel, headerData?.DiscountAbs, headerData?.Shipping]);

    const areButtonsDisabled = isSaving || loading || isRefreshing || isSyncingToCRM || isGeneratingPDF || isDuplicating;
    
    if (loading) return <LoadingSpinner text="Loading quote data..." />;
    if (error && !headerData) return <div style={{ padding: '20px', color: 'red' }}><h2>Error loading data:</h2><p>{error}</p> <button onClick={() => fetchQuoteData(quoteId)}>Try Again</button></div>;
    if (!headerData && !loading) return <div style={{ padding: '20px' }}><h2>No data for Quote ID: {quoteId}</h2></div>;
    
    return ( 
        <div className="quote-editor-wrapper" style={{ marginRight: selectedItem ? '400px' : '0' }}> 
            <div className="quote-editor-container">
                <div className="step-header" style={{ backgroundColor: getHeaderColor(currentStep) }}>
                    <div className="header-info">
                        <div className="header-title-line">
                            <a href={`https://crm.zoho.eu/crm/org20068501897/tab/Quotes/${headerData.CRM_Quote_ID}`} target="_blank" rel="noopener noreferrer" className="main-info-link" title="Open in Zoho CRM">
                                {headerData.Our_Quote_No || 'New Quote'}
                            </a>
                            <span className="quote-stage-badge">{headerData.Quote_Stage || 'N/A'}</span>
                        </div>
                        <span className="customer-name-header">For: <strong>{headerData.Account_Name || 'N/A'}</strong></span>
                    </div>
                    <StepIndicator 
                        currentStep={currentStep} 
                        onStepClick={handleGoToStep}
                        allowedSteps={allowedSteps}
                    />
                    <div className="logo-container">
                        <img src={quoteMachineLogo} alt="QuoteMachine Logo" className="logo" />
                    </div>
                </div>

                <div className={`step-container ${currentStep > 3 ? 'hidden' : ''}`}>
                    <div className="step-content">
                       <QuoteGrid
                            gridRef={gridRef}
                            rowData={rowData}
                            currentStep={currentStep}
                            userRole={userRole}
                            onCellValueChanged={handleCellValueChanged}
                            onRowDragEnd={onRowDragEnd}
                            onShowItemDetails={handleShowItemDetails}
                            onOpenTargetPriceModal={handleOpenTargetPriceModal}
                       />
                      <div className={`bottom-panels ${currentStep === 1 ? 'engineering-layout' : ''}`}>
                            {allowedSteps.includes(1) && (
                                <ItemActionsPanel 
                                    actionMenuConfig={actionMenuConfig}
                                    areButtonsDisabled={areButtonsDisabled}
                                    fileInputRef={fileInputRef}
                                />
                            )}
                        
                            {currentStep === 2 && (
                                <PurchasePanel 
                                    bulkOPDiscount={bulkOPDiscount}
                                    setBulkOPDiscount={setBulkOPDiscount}
                                    handleBulkUpdateOPDiscount={handleBulkUpdateOPDiscount}
                                    associatedCosts={associatedCosts}
                                    handleAssociatedCostChange={handleAssociatedCostChange}
                                    costDistribution={costDistribution}
                                    setCostDistribution={setCostDistribution}
                                    totals={totals}
                                    formatCurrencyDynamic={formatCurrencyDynamic}
                                    areButtonsDisabled={areButtonsDisabled}
                                />
                            )}

                            {currentStep === 3 && (
                                <SalesPanel
                                    targetMarginInput={targetMarginInput}
                                    setTargetMarginInput={setTargetMarginInput}
                                    handleSetTargetMargin={handleSetTargetMargin}
                                    bulkRPDiscount={bulkRPDiscount}
                                    setBulkRPDiscount={setBulkRPDiscount}
                                    handleBulkUpdateRPDiscount={handleBulkUpdateRPDiscount}
                                    bulkCPDiscount={bulkCPDiscount}
                                    setBulkCPDiscount={setBulkCPDiscount}
                                    handleBulkUpdateCPDiscount={handleBulkUpdateCPDiscount}
                                    bulkQuantity={bulkQuantity}
                                    setBulkQuantity={setBulkQuantity}
                                    handleBulkUpdateQuantity={handleBulkUpdateQuantity}
                                    headerData={headerData}
                                    handleHeaderChange={handleHeaderChange}
                                    totals={totals}
                                    formatCurrencyDynamic={formatCurrencyDynamic}
                                    areButtonsDisabled={areButtonsDisabled}
                                />
                            )}
                        </div>
                    </div>
                </div>

                <div className={`step-container ${currentStep !== 4 ? 'hidden' : ''}`}>
                     <div className="step-content">
                        <div className="panel validity-panel">
                            <h3 className="panel-title">Offer Settings</h3>
                            <div className="panel-content">
                                <div className="tool-group">
                                    <strong>Valid Till:</strong>
                                    <input type="date" name="Valid_Till" value={headerData.Valid_Till ? headerData.Valid_Till.split('T')[0] : ''} onChange={handleHeaderChange} className="validity-input"/>
                                </div>
                            </div>
                        </div>
                        <AddonTextsEditor texts={addonTexts} onTextsChange={setAddonTexts} templates={textTemplates} preferredLanguage={headerData.Preferred_Language || 'en'}/>
                    </div>
                </div>
            </div> 

            <TargetPriceModal
                isOpen={isTargetPriceModalOpen}
                onClose={handleCloseTargetPriceModal} // --- POUŽITÍ NOVÉ FUNKCE ---
                onSave={handleSaveTargetPrice}
                item={itemForTargetPriceModal}
            />

            <ItemDetailPanel item={selectedItem} onSave={handleSaveItemDetail} onClose={() => setSelectedItem(null)} /> 
            <NewItemModal isOpen={isNewItemModalOpen} onClose={() => setIsNewItemModalOpen(false)} onSave={handleAddNewItem} />
            <ProductSearchModal isOpen={isProductSearchOpen} onClose={() => setIsProductSearchOpen(false)} onProductSelect={handleProductSelect} />
            
            <div className="sticky-action-bar">
                <div className="sticky-action-bar-inner">
                    <div className="action-group-left">
                        <button 
                            onClick={handleSmartBack} 
                            className="btn-secondary" 
                            disabled={allowedSteps.indexOf(currentStep) === 0 || areButtonsDisabled}
                        >
                            Back
                        </button>
                        <button 
                            onClick={() => handleSaveChanges(false)} 
                            disabled={areButtonsDisabled}
                            className={saveBtnClass}
                            title={saveBtnTitle}
                        >
                            {isSaving ? 'Saving…' : (isLocalDirty ? 'Save (Local Changes)' : 'Save')}
                        </button>
                        <button 
                            onClick={handleGoToNext} 
                            disabled={areButtonsDisabled || allowedSteps.indexOf(currentStep) >= allowedSteps.length - 1}
                            className="btn-primary"
                        >
                            Next
                        </button>
                    </div>
                    <div className="action-group-center">
                        <button onClick={handleRefreshItemsFromCRM} disabled={areButtonsDisabled} className="btn-warning">
                            {isRefreshing ? 'Reloading...' : 'Reload CRM Data'}
                        </button>
                        <button onClick={handleResetChanges} disabled={areButtonsDisabled} className="btn-secondary">
                            Reset Changes
                        </button>
                    </div>
                    <div className="action-group-right">
                        {currentStep === 3 && (
                            <button onClick={handlePreviewPDF} disabled={areButtonsDisabled} className="btn-info">
                                {isGeneratingPDF ? 'Creating...' : 'PDF Preview'}
                            </button>
                        )}
                        {currentStep !== 3 && (
                            <button 
                                onClick={() => handleSyncItemsToCRM(false)} 
                                disabled={areButtonsDisabled || (headerData && !['Draft', 'Engineering'].includes(headerData.Quote_Stage)) || !(Array.isArray(rowData) && rowData.length > 0)} 
                                className={saveToCrmBtnClass}
                                title={saveToCrmBtnTitle}
                            >
                                {isSyncingToCRM ? 'Saving…' : (isCrmDirty ? 'Save to CRM (unsynced)' : 'Save to CRM (no PDF)')}
                            </button>
                        )}
                        {currentStep === 3 && (
                            <button 
                                onClick={() => handleSyncItemsToCRM(true)} 
                                disabled={areButtonsDisabled || (headerData && !['Draft', 'Engineering', 'Quotation'].includes(headerData.Quote_Stage)) || !(Array.isArray(rowData) && rowData.length > 0)} 
                                className={saveToCrmBtnClass}
                                title={saveToCrmBtnTitle}
                            >
                                {isSyncingToCRM ? 'Saving…' : (isCrmDirty ? 'Save to CRM & Publish (unsynced)' : 'Save to CRM & Publish PDF')}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div> 
    );
}

export default QuoteEditor;